import React, { useState } from "react";
import { Card, Row, Col, Tab, Tabs } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import { WorkBreakdownRow } from "../types/WorkBreakdownRow";
import useStyles from "../styles/useStyles";

interface TableSection {
  title: string;
  rows: WorkBreakdownRow[];
}

interface DataTableRow extends WorkBreakdownRow {
  collapsedData?: React.ReactNode;
}

interface Props {
  rateType: string;
  byLobData: TableSection[];
  byPhaseData: TableSection[];
  byLocationData: TableSection[];
  governanceRows: WorkBreakdownRow[];
  showLocationTab: boolean;
}

const Show: React.FC<Props> = ({
  rateType,
  byLobData,
  byPhaseData,
  byLocationData,
  governanceRows,
  showLocationTab,
}) => {
  const styles = useStyles();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const isSectionExpanded = (id: string) => expandedSections[id] ?? true;

  const renderDecimal = (val: number): string => {
    if (isNaN(val)) return "";
    return val.toFixed(2);
  };

  const detailColumns: ColumnDef<WorkBreakdownRow>[] = [
    {
      id: "service",
      header: "Service",
      cell: (props) => {
        const row = props.row.original;
        if (row.rowType === "subtotal") return null;
        if (row.rowType === "subservice")
          return <span style={{ paddingLeft: 16 }}>{row.service}</span>;
        return <span>{row.service}</span>;
      },
    },
    {
      id: "resource",
      header: "Resource",
      cell: (props) => {
        const row = props.row.original;
        if (row.rowType === "subtotal") return null;
        return <span>{row.resource}</span>;
      },
    },
    {
      id: "quantity",
      header: "Quantity",
      cell: (props) => {
        const row = props.row.original;
        if (row.rowType === "subtotal") return null;
        return <span>{row.quantity}</span>;
      },
    },
    {
      id: "hours",
      header: rateType,
      cell: (props) => {
        const row = props.row.original;
        if (row.rowType === "subtotal")
          return (
            <span style={{ fontWeight: 700 }}>
              Total: {renderDecimal(row.hours)}
            </span>
          );
        return <span>{renderDecimal(row.hours)}</span>;
      },
    },
    {
      id: "total",
      header: "Total",
      cell: (props) => {
        const row = props.row.original;
        if (row.rowType === "subtotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {renderDecimal(row.total)}
            </span>
          );
        return <span>{renderDecimal(row.total)}</span>;
      },
    },
  ];

  const buildColumns = (): ColumnDef<DataTableRow>[] => [
    {
      id: "service",
      header: "Service",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "grandTotal") return null;
        return <span>{row.service}</span>;
      },
    },
    {
      id: "resource",
      header: "Resource",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "grandTotal") return null;
        return <span>{row.resource}</span>;
      },
    },
    {
      id: "quantity",
      header: "Quantity",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "grandTotal") return null;
        if (row.id.startsWith("summary-")) return <span>---</span>;
        return <span>{row.quantity}</span>;
      },
    },
    {
      id: "hours",
      header: rateType,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "grandTotal")
          return (
            <span style={{ fontWeight: 700 }}>Grand Total:</span>
          );
        return <span>{renderDecimal(row.hours)}</span>;
      },
    },
    {
      id: "total",
      header: "Total",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "grandTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {renderDecimal(row.total)}
            </span>
          );
        return <span>{renderDecimal(row.total)}</span>;
      },
    },
  ];

  const buildTableRows = (rows: WorkBreakdownRow[]): DataTableRow[] => {
    const result: DataTableRow[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (row.rowType === "data") {
        const subRows: WorkBreakdownRow[] = [];
        let j = i + 1;
        while (j < rows.length && rows[j].rowType === "subservice") {
          subRows.push(rows[j]);
          j++;
        }
        const hasSubtotal =
          j < rows.length && rows[j].rowType === "subtotal";

        const subtotalRow = hasSubtotal ? rows[j] : null;
        const detailRows: WorkBreakdownRow[] =
          subRows.length > 0 ? [row, ...subRows] : [row];
        result.push({
          ...row,
          id: `summary-${row.id}`,
          service: `${row.service} Summary`,
          resource: row.summaryResource || "---",
          quantity: 0,
          hours: subtotalRow ? subtotalRow.hours : row.hours,
          total: subtotalRow ? subtotalRow.total : row.total,
          collapsedData: (
            <DataTable
              data={detailRows}
              columns={detailColumns}
              selectable={false}
              classForTable={`${styles.detailTable} ${styles.hideDetailHead}`}
              striped
            />
          ),
        });
        if (hasSubtotal) {
          i = j;
        } else if (subRows.length > 0) {
          i = j - 1;
        }
      } else if (row.rowType === "grandTotal") {
        result.push({ ...row });
      } else if (row.rowType !== "subservice") {
        result.push({ ...row });
      }
    }
    return result;
  };

  const hasDataRows = (rows: WorkBreakdownRow[]) =>
    rows.some((r) => r.rowType === "data" || r.rowType === "subservice");

  const renderSection = (
    key: string,
    title: string,
    rows: WorkBreakdownRow[]
  ) => {
    const sectionExpanded = isSectionExpanded(key);
    const tableRows = buildTableRows(rows);
    const hasCollapsible = tableRows.some((r) => r.collapsedData);

    return (
      <div key={key} className={styles.section}>
        <div
          className={styles.sectionHeader}
          onClick={() => toggleSection(key)}
        >
          <span className={styles.sectionTitle}>{title}</span>
          <FontAwesomeIcon
            icon={sectionExpanded ? faChevronUp : faChevronDown}
            className={styles.sectionChevron}
          />
        </div>
        {sectionExpanded && (
          <div className={styles.sectionBody}>
            {hasDataRows(rows) ? (
              <DataTable
                data={tableRows}
                columns={buildColumns()}
                selectable={false}
                isCollapsible={hasCollapsible}
                classForTable={styles.sectionTable}
                striped
              />
            ) : (
              <div style={{ padding: "16px", color: "#6c757d" }}>
                No services found
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSections = (sections: TableSection[]) =>
    sections
      .filter((s) => hasDataRows(s.rows))
      .map((s) => renderSection(s.title, s.title, s.rows));

  return (
    <div className={`${styles.page} ${styles.columnWidths}`}>
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Work Breakdown</Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} style={{ marginBottom: 4 }} />
        <Card.Body className={styles.pricingTabs}>
          <Tabs defaultActiveKey="byLob" className="mb-3">
            <Tab eventKey="byLob" title="By LOB">
              <div className={styles.tabContent}>
                {renderSections(byLobData)}
                {hasDataRows(governanceRows) &&
                  renderSection(
                    "governance",
                    "Project Governance",
                    governanceRows
                  )}
              </div>
            </Tab>

            <Tab eventKey="byPhase" title="By Phase">
              <div className={styles.tabContent}>
                {renderSections(byPhaseData)}
                {hasDataRows(governanceRows) &&
                  renderSection(
                    "governance",
                    "Project Governance",
                    governanceRows
                  )}
              </div>
            </Tab>

            {showLocationTab && (
              <Tab eventKey="byLocation" title="By Location">
                <div className={styles.tabContent}>
                  {renderSections(byLocationData)}
                  {hasDataRows(governanceRows) &&
                    renderSection(
                      "governance",
                      "Project Governance",
                      governanceRows
                    )}
                </div>
              </Tab>
            )}
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Show;
